import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography, Container, Chip, Box,
  GlobalStyles, Link, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Autocomplete,
  Snackbar, Alert, useTheme, useMediaQuery
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { debounce } from 'lodash';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';

interface JobApplication {
  id: string;
  company: string;
  position: string;
  location: string;
  salary: string;
  salaryType?: 'yearly' | 'hourly';
  hoursPerWeek?: number;
  status: 'Applied' | 'Interviewing' | 'Hired' | 'Rejected';
  dateapplied: string;
  link?: string;
}

const LocationApplicationsList: React.FC = () => {
  const { location } = useParams<{ location: string }>();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('dateapplied');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [editingApp, setEditingApp] = useState<JobApplication | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const inferSalaryType = (salary: string): 'yearly' | 'hourly' => {
    if (salary.toLowerCase().includes('/hr') || salary.toLowerCase().includes('hour')) {
      return 'hourly';
    }
    const numericSalary = parseFloat(salary.replace(/[^0-9.]/g, ''));
    return numericSalary < 1000 ? 'hourly' : 'yearly';
  };

  useEffect(() => {
    fetchApplications();
  }, [location]);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/location/${encodeURIComponent(location || '')}`, {
        headers: {
          'x-auth-token': localStorage.getItem('token') || '',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      const data = await response.json();
      const applicationsWithSalaryType = data.applications.map((app: JobApplication) => ({
        ...app,
        salaryType: app.salaryType || inferSalaryType(app.salary),
      }));
      setApplications(applicationsWithSalaryType);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications. Please try again.');
      setApplications([]);
      setLoading(false);
    }
  };

  const fieldToSortFieldMap: { [key: string]: string } = {
    'Company': 'company',
    'Position': 'position',
    'Yearly Salary': 'yearlysalary',
    'Hourly Salary': 'hourlysalary',
    'Status': 'status',
    'Date Applied': 'dateapplied',
  };

  const getStatusColor = (status: JobApplication['status']) => {
    switch (status) {
      case 'Applied':
        return 'default';
      case 'Interviewing':
        return 'primary';
      case 'Hired':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEdit = (app: JobApplication) => {
    setEditingApp(app);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}`, {
          method: 'DELETE',
          headers: {
            'x-auth-token': localStorage.getItem('token') || '',
          },
        });
        if (!response.ok) {
          throw new Error('Failed to delete application');
        }
        await fetchApplications();
        setSnackbarMessage('Application deleted successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        console.error('Error deleting application:', error);
        setSnackbarMessage('Failed to delete application. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    }
  };

  const handleSave = async () => {
    if (editingApp) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${editingApp.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': localStorage.getItem('token') || '',
          },
          body: JSON.stringify(editingApp),
        });
        if (!response.ok) {
          throw new Error('Failed to update application');
        }
        await fetchApplications();
        setIsDialogOpen(false);
        setSnackbarMessage('Application updated successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        console.error('Error updating application:', error);
        setSnackbarMessage('Failed to update application. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditingApp(prev =>
      prev ? { ...prev, [name]: value } : null
    );
  };

  const handleStatusChange = (e: SelectChangeEvent<JobApplication['status']>) => {
    setEditingApp(prev => (prev ? { ...prev, status: e.target.value as JobApplication['status'] } : null));
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const parseSalaryValue = (salary: string): number => {
    const numericString = salary.replace(/[^0-9.]/g, '');
    const value = parseFloat(numericString);
    return isNaN(value) ? 0 : value;
  };

  const sortedApplications = [...applications].sort((a, b) => {
    if (sortField === 'salary') {
      const aSalary = parseSalaryValue(a.salary);
      const bSalary = parseSalaryValue(b.salary);
      return sortDirection === 'asc' ? aSalary - bSalary : bSalary - aSalary;
    }
    if (sortField in a && sortField in b) {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
    }
    return 0;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return format(date, 'MM/dd/yyyy');
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const handleSalaryTypeChange = (e: SelectChangeEvent<'yearly' | 'hourly'>) => {
    setEditingApp(prev => (prev ? { ...prev, salaryType: e.target.value as 'yearly' | 'hourly' } : null));
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const fetchSuggestions = async (query: string) => {
    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
    const params = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      types: 'place',
      limit: '5',
    });

    const response = await fetch(`${endpoint}?${params}`);
    const data = await response.json();
    return data.features.map((feature: any) => feature.place_name);
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(async (input: string) => {
      if (input.length > 2) {
        const suggestions = await fetchSuggestions(input);
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    }, 300),
    []
  );

  const handleLocationChange = (event: any, value: string | null) => {
    const newLocation = value === 'Remote' || value === 'N/A' ? value : value || '';
    setEditingApp(prev => (prev ? { ...prev, location: newLocation } : null));
  };

  return (
    <Container maxWidth="lg">
      <GlobalStyles
        styles={{
          '.simplebar-scrollbar:before': {
            background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
          },
          '.simplebar-scrollbar.simplebar-visible:before': {
            opacity: 1,
          },
          '.simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
            height: '8px',
          },
          '.simplebar-track.simplebar-vertical .simplebar-scrollbar': {
            width: '8px',
          },
          '.simplebar-content-wrapper': {
            scrollBehavior: 'smooth',
          },
        }}
      />
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ color: '#3a7bd5' }}>
          Applications in {decodeURIComponent(location || '')}
        </Typography>
      </Box>
      {loading ? (
        <Typography>Loading applications...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <SimpleBar style={{ maxHeight: isMobile ? 'calc(100vh - 200px)' : 'none' }} forceVisible="x" autoHide={false}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(to right, #3a7bd5, #00d2ff)' }}>
                  {['Company', 'Position', 'Yearly Salary', 'Hourly Salary', 'Status', 'Date Applied', 'Actions'].map((field) => (
                    <TableCell
                      key={field}
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        whiteSpace: 'nowrap',
                        padding: isMobile ? '8px 4px' : '16px',
                      }}
                      onClick={() => handleSort(field)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {isMobile ? field.charAt(0) : field}
                        {field !== 'Actions' && (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                            <ArrowUpward
                              sx={{
                                fontSize: 18,
                                opacity: sortField === fieldToSortFieldMap[field] && sortDirection === 'asc' ? 1 : 0.3,
                                transition: 'opacity 0.2s',
                              }}
                            />
                            <ArrowDownward
                              sx={{
                                fontSize: 18,
                                opacity: sortField === fieldToSortFieldMap[field] && sortDirection === 'desc' ? 1 : 0.3,
                                transition: 'opacity 0.2s',
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedApplications.map((app) => (
                  <TableRow key={app.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.link ? (
                        <Link href={app.link} target="_blank" rel="noopener noreferrer" sx={{ color: '#3a7bd5', textDecoration: 'none' }}>
                          {app.company}
                        </Link>
                      ) : (
                        app.company
                      )}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>{app.position}</TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.salaryType === 'yearly' && app.salary ? `$${app.salary}` : '--'}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.salaryType === 'hourly' && app.salary ? `$${app.salary}/hr` : '--'}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      <Chip label={app.status} color={getStatusColor(app.status)} size={isMobile ? 'small' : 'medium'} />
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>{formatDate(app.dateapplied)}</TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton
                          onClick={() => handleEdit(app)}
                          size="small"
                          sx={{ color: '#3a7bd5', p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(app.id)}
                          size="small"
                          sx={{ color: '#ff4081', p: 0.5, ml: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SimpleBar>
        </Paper>
      )}

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Edit Application</DialogTitle>
        <DialogContent>
          {editingApp && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                fullWidth
                label="Company"
                name="company"
                value={editingApp.company}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={editingApp.position}
                onChange={handleInputChange}
              />
              <Autocomplete
                freeSolo
                options={['Remote', 'N/A', ...locationSuggestions]}
                value={editingApp.location}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue !== 'Remote' && newInputValue !== 'N/A') {
                    debouncedFetchSuggestions(newInputValue);
                  }
                }}
                onChange={handleLocationChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Location"
                    name="location"
                  />
                )}
              />
              <TextField
                fullWidth
                label="Salary"
                name="salary"
                value={editingApp.salary}
                onChange={handleInputChange}
              />
              <FormControl fullWidth>
                <InputLabel>Salary Type</InputLabel>
                <Select
                  name="salaryType"
                  value={editingApp.salaryType || ''}
                  onChange={handleSalaryTypeChange}
                >
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                </Select>
              </FormControl>
              {editingApp.salaryType === 'hourly' && (
                <TextField
                  fullWidth
                  label="Hours per Week"
                  name="hoursPerWeek"
                  type="number"
                  value={editingApp.hoursPerWeek || ''}
                  onChange={handleInputChange}
                />
              )}
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editingApp.status}
                  onChange={handleStatusChange}
                  label="Status"
                >
                  <MenuItem value="Applied">Applied</MenuItem>
                  <MenuItem value="Interviewing">Interviewing</MenuItem>
                  <MenuItem value="Hired">Hired</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Date Applied"
                name="dateapplied"
                type="date"
                value={format(parseISO(editingApp.dateapplied), 'yyyy-MM-dd')}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Job Link"
                name="link"
                value={editingApp.link || ''}
                onChange={handleInputChange}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LocationApplicationsList;